"use client";

import type { CSSProperties } from "react";
import { useState } from "react";
import {
	ArtistIcon,
	GlassButton,
	GlassTextField,
	SheetHeader,
} from "@/components/design-system/ArtistDesignSystem";

export interface CreatePlaylistSheetProps {
	readonly onClose: () => void;
	readonly onCreate: (name: string, description: string | null) => void;
}

const overlayStyle: CSSProperties = {
	position: "fixed",
	inset: 0,
	zIndex: 80,
	display: "grid",
	alignItems: "end",
	justifyItems: "center",
	padding: 0,
	background: "rgb(0 0 0 / 0.6)",
	backdropFilter: "blur(8px)",
	WebkitBackdropFilter: "blur(8px)",
};

const sheetStyle: CSSProperties = {
	width: "100%",
	maxWidth: 560,
	maxHeight: "85dvh",
	overflow: "auto",
	borderTopLeftRadius: 28,
	borderTopRightRadius: 28,
	borderBottomLeftRadius: 0,
	borderBottomRightRadius: 0,
	background: "var(--ds-surface)",
	boxShadow: "0 -20px 60px rgb(0 0 0 / 0.35)",
};

const bodyStyle: CSSProperties = {
	display: "grid",
	gap: 16,
	padding: "16px 20px 32px",
};

/**
 * CreatePlaylistSheet — slide-over sheet for creating a new playlist.
 * Mirrors `createPlaylistSheet` on iOS (NavigationStack + name field +
 * optional description field + full-width create button + cancel toolbar).
 */
export function CreatePlaylistSheet({
	onClose,
	onCreate,
}: Readonly<CreatePlaylistSheetProps>) {
	const [name, setName] = useState("");
	const [description, setDescription] = useState("");

	const trimmedName = name.trim();
	const canCreate = trimmedName.length > 0;

	const handleCreate = () => {
		if (!canCreate) return;
		const trimmedDescription = description.trim();
		onCreate(
			trimmedName,
			trimmedDescription.length === 0 ? null : trimmedDescription,
		);
	};

	return (
		<div
			className="library-sheet-overlay"
			style={overlayStyle}
			role="dialog"
			aria-modal="true"
			aria-label="创建歌单"
			onClick={onClose}
			onKeyDown={(event) => {
				if (event.key === "Escape") onClose();
			}}
		>
			{/* biome-ignore lint/a11y/useKeyWithClickEvents: stopPropagation prevents backdrop close when clicking inside the sheet; not a keyboard-interactive element */}
			{/* biome-ignore lint/a11y/noStaticElementInteractions: presentational sheet container; click handler only prevents event bubbling to the overlay */}
			<div
				className="library-sheet"
				style={sheetStyle}
				onClick={(event) => event.stopPropagation()}
			>
				<span
					aria-hidden="true"
					style={{
						width: 42,
						height: 5,
						margin: "10px auto 0",
						borderRadius: "var(--ds-radius-full)",
						background: "var(--ds-icon-secondary)",
						display: "block",
					}}
				/>
				<SheetHeader
					title="新建歌单"
					leading="cancel"
					leadingLabel="关闭"
					onLeading={onClose}
				/>
				<div style={bodyStyle}>
					<GlassTextField
						label="名称"
						leading={<ArtistIcon name="music" size={16} strokeWidth={1.8} />}
						placeholder="给歌单起个名字"
						value={name}
						onChange={(event) => setName(event.target.value)}
						maxLength={60}
					/>
					<GlassTextField
						label="描述（可选）"
						leading={<ArtistIcon name="message" size={16} strokeWidth={1.8} />}
						placeholder="一句话描述这个歌单"
						value={description}
						onChange={(event) => setDescription(event.target.value)}
						maxLength={120}
					/>
					<GlassButton
						kind="primary"
						size="md"
						expands
						isEnabled={canCreate}
						onClick={handleCreate}
					>
						创建
					</GlassButton>
				</div>
			</div>
		</div>
	);
}
